/**
 * Zentrale Textbibliothek für die ZAVI Website
 *
 * Diese Datei enthält alle Texte der Website in einer strukturierten Form.
 * Durch die Zentralisierung der Texte wird die Wartbarkeit verbessert und
 * die Konsistenz über die gesamte Website hinweg sichergestellt.
 */

export const texts = {
  // Globale Komponenten
  global: {
    // Schema

    // Metadata
    metadata: {
      site: {
        name: "ZAVI",
        description:
          "Wir bringen KI direkt in deine Prozesse – maßgeschneidert, sicher und DSGVO-konform. Steigere deine Effizienz mit KI-Lösungen Made in Germany.",
        url: "https://zavi-ai.de",
        ogImage: "/ZAVI_OG.png",
        themeColor: "#f5f9ff",
        locale: "de_DE",
        type: "website",
      },
      links: {
        twitter: "https://twitter.com/zavi_de",
        linkedin: "https://linkedin.com/company/zavi-ai",
        instagram: "https://www.instagram.com/zavi_ai",
        tiktok: "https://www.tiktok.com/@zavi_ai",
      },
      keywords: [
        "KI",
        "Künstliche Intelligenz",
        "Automatisierung",
        "Mittelstand",
        "Prozessoptimierung",
        "Chatbots",
        "Effizienzsteigerung",
        "B2B-Lösungen",
        "Deutsche KI-Lösungen",
        "KI-Beratung",
        "KI-Implementierung",
        "KI-Partnerschaft",
        "DSGVO-konforme KI",
        "KI Made in Germany",
      ],
      author: {
        name: "ZAVI GmbH",
        url: "https://zavi-ai.de",
      },
      creator: "ZAVI GmbH",
      twitter: {
        handle: "@zavi_de",
      },
    },

    // Navigation
    navbar: {
      logo: {
        url: "/",
        src: "/logos/zavi.svg",
        alt: "logo",
        title: "ZAVI",
      },
      menu: [
        {
          title: "Dienstleistungen",
          url: "/dienstleistungen",
          items: [
            {
              title: "KI-Beratung",
              description:
                "Expertenberatung und strategische KI-Lösungen für Ihr Unternehmen",
              url: "/beratung",
            },
            {
              title: "KI-Implementierung",
              description:
                "Professionelle Entwicklung und Integration von KI-Lösungen",
              url: "/implementierung",
            },
          ],
        },
        {
          title: "News",
          url: "/news",
        },
        {
          title: "Kontakt",
          url: "/kontakt",
        },
      ],
      mobileMenu: {
        openLabel: "Öffne Menü",
        closeLabel: "Schließe Menü",
      },
      accessibility: {
        submenuLinks: "Untermenü-Links",
      },
    },

    // Footer
    footer: {
      sections: [
        {
          title: "Dienstleistungen",
          links: [
            { name: "KI-Beratung", href: "/beratung" },
            { name: "KI-Implementierung", href: "/implementierung" },
          ],
        },
        {
          title: "Unternehmen",
          links: [
            { name: "Projekte", href: "/projekte" },
            { name: "Über uns", href: "/ueber-uns" },
            { name: "Kontakt", href: "/kontakt" },
            { name: "Karriere", href: "/karriere" },
          ],
        },
        {
          title: "Inhalte",
          links: [
            { name: "News", href: "/news" },
            { name: "Analysen", href: "/analysen" },
          ],
        },
        {
          title: "Rechtliches",
          links: [
            { name: "Impressum", href: "/impressum" },
            { name: "Datenschutz", href: "/datenschutz" },
            { name: "AGB", href: "/agb" },
          ],
        },
      ],
      social: {
        title: "Social",
        links: [
          {
            name: "LinkedIn",
            href: "https://www.linkedin.com/company/zavi-ai/",
          },
          {
            name: "Instagram",
            href: "https://www.instagram.com/zavi_ai",
          },
          {
            name: "Twitter",
            href: "https://x.com/zavi_de",
          },
          {
            name: "TikTok",
            href: "https://www.tiktok.com/@zavi_ai",
          },
        ],
        followText:
          "Folge uns, um über neueste KI-Entwicklungen informiert zu bleiben",
      },
      logo: {
        url: "/",
        src: "/logos/zavi.svg",
        alt: "ZAVI Logo",
      },
      copyright: {
        text: "Alle Rechte vorbehalten.",
      },
      cookies: {
        settingsText: "Cookie-Einstellungen",
      },
    },

    // Cookie Consent
    cookies: {
      banner: {
        title: "Cookie-Einstellungen",
        description:
          "Diese Website verwendet Cookies, um Ihnen ein besseres Erlebnis zu bieten.",
        closeAriaLabel: "Cookie-Banner schließen",
        buttons: {
          acceptAll: "Alle akzeptieren",
          decline: "Ablehnen",
          settings: "Einstellungen",
        },
      },
      settings: {
        title: "Cookie-Einstellungen",
        description:
          "Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Hier können Sie Ihre Cookie-Präferenzen verwalten.",
        intro:
          "Wir verwenden Cookies auf unserer Website, um Ihnen die relevanteste Erfahrung zu bieten. Sie können wählen, welche Art von Cookies Sie akzeptieren möchten.",
        cookieTypes: {
          analytics: {
            title: "Analytics Cookies (Google Analytics)",
            description:
              "Diese Cookies ermöglichen es uns, Besuche und Verkehrsquellen zu zählen, damit wir die Leistung unserer Website messen und verbessern können.",
          },
          ads: {
            title: "Werbe-Cookies (Google Ads)",
            description:
              "Diese Cookies werden verwendet, um Daten zu Werbezwecken zu erheben. Sie ermöglichen es uns, relevantere Anzeigen zu schalten. <strong>Diese Einstellung aktiviert auch die Nutzerdaten für Werbemessung und personalisierte Anzeigen.</strong>",
          },
          adUserData: {
            title: "Nutzerdaten für Werbemessung",
            description:
              "Diese Einstellung erlaubt die Erhebung von Nutzerdaten zur Erfolgsmessung bei Anzeigen und für Conversion-Tracking.<em> (Wird automatisch mit Werbe-Cookies aktiviert)</em>",
          },
          adPersonalization: {
            title: "Personalisierte Anzeigen",
            description:
              "Diese Einstellung erlaubt es, Ihre Daten für personalisierte Anzeigen und Remarketing-Zielgruppen zu verwenden.<em> (Wird automatisch mit Werbe-Cookies aktiviert)</em>",
          },
          turnstile: {
            title: "Sicherheits-Cookies (Cloudflare Turnstile)",
            description:
              "Diese Cookies werden von Cloudflare Turnstile gesetzt, einem Dienst zur Erkennung von Bots. Sie helfen uns, unsere Website vor Spam und automatisierten Missbrauch zu schützen.",
          },
          preferences: {
            title: "Präferenz-Cookies",
            description:
              "Diese Cookies ermöglichen es der Website, verbesserte Funktionalität und Personalisierung bereitzustellen.",
          },
        },
        footer: {
          privacyText: "Für weitere Informationen lesen Sie bitte unsere",
          privacyLink: "Datenschutzerklärung",
          privacyUrl: "/datenschutz",
        },
        buttons: {
          decline: "Ablehnen",
          saveSelection: "Auswahl speichern",
          acceptAll: "Alle akzeptieren",
        },
      },
    },

    // Call-to-Action
    cta: {
      badge: {
        text: "Potenzial",
      },
      heading: "Bereit für den nächsten Schritt?",
      description:
        "Entdecke in nur 10 Minuten, wie KI dein Unternehmen auf das nächste Level bringen kann. Sichere dir jetzt deinen personalisierten Fahrplan in die KI-Zukunft.",
      comingSoon: "Coming Soon",
      button: {
        text: "Starte deine KI-Potenzialanalyse",
      },
      image: {
        src: "/cta/cta.png",
        alt: "KI Potenzialanalyse",
      },
    },
  },

  // Seiten
  pages: {
    // Not Found
    notFound: {},

    // Startseite
    home: {
      metadata: {
        title: "Wir machen dein Unternehmen zukunftssicher mit KI",
        description:
          "Wir bringen KI direkt in deine Prozesse – maßgeschneidert, sicher und DSGVO-konform. Steigere deine Effizienz mit KI-Lösungen Made in Germany.",
      },
      hero: {
        heading: "Wir machen dein Unternehmen zukunftssicher",
        subheading:
          "Wir bringen KI direkt in deine Prozesse – maßgeschneidert, sicher und ohne Umwege",
      },
      features: {
        badge: "Innovation",
        heading: "Was wir machen",
        description:
          "ZAVI macht Künstliche Intelligenz greifbar für dein Unternehmen. Wir entwickeln maßgeschneiderte Lösungen, die sich nahtlos in deine bestehenden Prozesse integrieren.",
        feature1: {
          title: "Sofortige Effizienzsteigerung",
          description:
            "Messbare Resultate ab Tag 1. Unsere KI-Lösungen zeigen unmittelbar positive Auswirkungen auf deine Geschäftsprozesse.",
          image: "/home/home_1.png",
        },
        feature2: {
          title: "DSGVO-konform",
          description:
            "Made in Germany, selbst gehostet, volle Kontrolle. Deine Daten bleiben da, wo sie hingehören – in deinem Unternehmen.",
          image: "/home/home_2.png",
        },
        feature3: {
          title: "Keine Systemumstellung",
          description:
            "Direkte Integration in deine bestehende IT-Landschaft. Wir passen uns an deine Systeme an, nicht umgekehrt.",
          image: "/home/home_3.png",
        },
        feature4: {
          title: "Zukunftssicher investieren",
          description:
            "Skalierbare Lösungen, die mit dir wachsen. Bleib der Konkurrenz einen Schritt voraus mit kontinuierlichen Updates.",
          image: "/home/home_4.png",
        },
      },
      timeline: {
        badge: "Umsetzung",
        heading: "So machen wir dich fit für die KI-Zukunft",
        description:
          "Wir begleiten dich von der ersten Idee bis zum laufenden KI-Betrieb. Mit unseren drei Kernleistungen stellen wir sicher, dass du nachhaltig von KI profitierst.",
        items: [
          {
            title: "KI Beratung",
            description:
              "Wir analysieren deine Prozesse und identifizieren, wo KI den größten Mehrwert schafft. So weißt du genau, welche Schritte sinnvoll sind – jetzt und in Zukunft.",
            image: {
              src: "/home/home_5.png",
              alt: "KI Beratung",
            },
          },
          {
            title: "KI-Integration",
            description:
              "Maßgeschneiderte Implementation direkt in deine Systeme – ohne komplizierte Drittsoftware. Von der Buchhaltung bis zum Kundenservice, wir automatisieren dort, wo es sich lohnt.",
            image: {
              src: "/home/home_6.png",
              alt: "KI-Integration",
            },
            reverse: true,
          },
        ],
      },
      solutions: {
        badge: "KI-Lösungen",
        heading: "Deine Möglichkeiten mit KI",
        description:
          "Entdecke unsere innovativen KI-Lösungen, die dein Unternehmen auf das nächste Level heben. Von intelligenten Assistenten bis zur vollautomatischen Datenverarbeitung – wir machen KI für dich nutzbar.",
        items: [
          {
            title: "Intelligenter KI-Chatbot",
            description:
              "Entwickle deinen KI-Assistenten, der deine Kunden rund um die Uhr betreut. Personalisiert, mehrsprachig und nahtlos in deine Systeme integriert – für Service der begeistert.",
          },
          {
            title: "KI-Sprachassistent",
            description:
              "Steuere Prozesse per Sprache. Unser KI-Sprachassistent versteht deine Anweisungen und setzt sie direkt um – für eine natürliche und effiziente Arbeitsweise.",
          },
          {
            title: "Smarte Wissensdatenbank",
            description:
              "Mach dein Unternehmenswissen intelligent. Mit KI-gestützter Dokumentation, die Informationen sofort findet und automatisch aktuell hält – damit dein Team immer bestens informiert ist.",
          },
          {
            title: "Automatische Datenverarbeitung",
            description:
              "Vergiss manuelle Dateneingabe. Unsere KI erfasst, verarbeitet und pflegt deine Daten automatisch ein – schnell, präzise und ohne Tippfehler.",
          },
          {
            title: "KI-Content Automation",
            description:
              "Erstelle Inhalte auf Knopfdruck. Von Produktbeschreibungen bis zu Marketingtexten – unsere KI generiert maßgeschneiderten Content in deiner Unternehmenssprache.",
          },
          {
            title: "Individuelle Software-Integration",
            description:
              "Verbinde deine Systeme intelligent. Wir entwickeln die perfekte KI-Lösung für deine Anforderungen und integrieren sie nahtlos in deine bestehende Software.",
          },
          {
            title: "Prozess-Automatisierung",
            description:
              "Optimiere deine Arbeitsabläufe mit KI. Automatisiere Routineaufgaben und schaffe effiziente Prozesse – damit sich dein Team auf das Wesentliche konzentrieren kann.",
          },
        ],
      },
    },

    // Beratung
    beratung: {
      metadata: {
        title: "KI-Beratung für deine digitale Transformation",
        description:
          "Erhalte einen maßgeschneiderten KI-Fahrplan für dein Unternehmen. Wir analysieren deine Prozesse und zeigen dir konkrete Optimierungspotenziale.",
      },

      problems: {
        heading: "KI-Beratung für echte Herausforderungen",
        subheading:
          "Wir helfen dir, die Potenziale von KI für dein Unternehmen zu erkennen und gezielt zu nutzen",
        badge: "Herausforderungen",
        title: "Diese Probleme lösen wir gemeinsam",
        description:
          "Viele Unternehmen stehen vor ähnlichen Herausforderungen bei der KI-Einführung. Wir helfen dir, diese zu meistern",
        items: [
          {
            title: "Orientierungslosigkeit",
            description:
              "Der KI-Markt ist unübersichtlich, Anbieter versprechen das Blaue vom Himmel. Welche KI-Lösungen bringen wirklich Mehrwert für dein Unternehmen?",
          },
          {
            title: "Ressourcenverschwendung",
            description:
              "Mitarbeiter verbringen 40% ihrer Zeit mit repetitiven Aufgaben. Qualifizierte Fachkräfte können sich nicht auf wertschöpfende Tätigkeiten konzentrieren.",
          },
          {
            title: "Implementierungsängste",
            description:
              "Bestehende Systeme sind komplex gewachsen. Wie integrieren wir KI ohne unsere funktionierenden Prozesse zu gefährden?",
          },
          {
            title: "Zukunftsunsicherheit",
            description:
              "KI-Entwicklung schreitet rasant voran. Wie stellen wir sicher, dass wir nicht den Anschluss verlieren?",
          },
        ],
      },
      services: {
        badge: "Lösungen",
        title: "Unsere KI-Beratung für deinen Erfolg",
        description:
          "Mit unseren vier Kernleistungen begleiten wir dich von der ersten Analyse bis zur erfolgreichen KI-Integration",
        items: [
          {
            title: "Potenzialanalyse",
            description:
              "Wir identifizieren die wichtigsten KI-Chancen in deinem Unternehmen. Konkrete Handlungsempfehlungen statt vager Versprechen.",
          },
          {
            title: "Maßgeschneiderte Roadmap",
            description:
              "Klarer Fahrplan für deine KI-Integration. Priorisierte Maßnahmen mit ROI-Berechnung und konkreten Meilensteinen.",
          },
          {
            title: "Sicherheitskonzept",
            description:
              "DSGVO-konforme Implementierung von Anfang an. Made in Germany - deine Daten bleiben sicher in deiner Kontrolle.",
          },
          {
            title: "Zukunftssicherung",
            description:
              "Kontinuierliche Beratung zu neuen KI-Entwicklungen. Proaktive Anpassung deiner KI-Strategie für langfristigen Erfolg.",
          },
        ],
      },
      stats: {
        badge: "Erfolge",
        title: "Messbare Erfolge durch KI-Beratung",
        description:
          "Unsere Kunden profitieren von nachweisbaren Verbesserungen in Effizienz, Kosten und Mitarbeiterzufriedenheit",
        items: [
          {
            title: "Effizienzsteigerung durch KI",
            value: "40%",
            subtitle: "Zeitersparnis",
          },
          {
            title: "Durchschnittliche Kosteneinsparung",
            value: "30%",
            subtitle: "pro Jahr",
          },
          {
            title: "Mitarbeiterzufriedenheit",
            value: "90%",
            subtitle: "positives Feedback",
          },
        ],
      },
      process: {
        badge: "Prozess",
        title: "So läuft deine KI-Beratung ab",
        description:
          "Von der ersten Analyse bis zur erfolgreichen Umsetzung begleiten wir dich Schritt für Schritt auf deinem Weg zur KI-Integration",
        steps: [
          {
            number: 1,
            title: "Erstgespräch & Analyse",
            description:
              "Wir verstehen deine Herausforderungen und bieten dir eine kostenlose Ersteinschätzung deines KI-Potenzials.",
            image: {
              src: "/consulting/consulting_1.png",
              alt: "Erstgespräch & Analyse",
            },
          },
          {
            number: 2,
            title: "Potenzialanalyse",
            description:
              "Detaillierte Untersuchung deiner Prozesse und Identifikation der größten KI-Chancen in deinem Unternehmen.",
            image: {
              src: "/consulting/consulting_2.png",
              alt: "Potenzialanalyse",
            },
          },
          {
            number: 3,
            title: "Strategieentwicklung",
            description:
              "Entwicklung eines maßgeschneiderten Fahrplans mit konkreten Maßnahmen und klarer Timeline für deine KI-Integration.",
            image: {
              src: "/consulting/consulting_3.png",
              alt: "Strategieentwicklung",
            },
          },
        ],
      },
      faq: {
        badge: "FAQ",
        title: "Häufig gestellte Fragen zur KI-Beratung",
        description:
          "Hier findest du Antworten auf die wichtigsten Fragen rund um unsere KI-Beratung",
        items: [
          {
            question: "Wie lange dauert eine KI-Beratung?",
            answer:
              "Die Dauer variiert je nach Umfang und Komplexität deiner Anforderungen. Typischerweise erstellen wir innerhalb von 2-4 Wochen eine erste Analyse mit konkreten Handlungsempfehlungen.",
          },
          {
            question: "Was kostet die KI-Beratung?",
            answer:
              "Die Kosten richten sich nach dem individuellen Beratungsumfang. Das Erstgespräch und die initiale Potenzialeinschätzung sind kostenlos. Danach erstellen wir dir ein maßgeschneidertes Angebot.",
          },
          {
            question: "Brauchen wir Vorkenntnisse im Bereich KI?",
            answer:
              "Nein, keine Sorge! Wir erklären dir alles verständlich und praxisnah. Unser Ziel ist es, KI greifbar und nutzbar zu machen - auch ohne technisches Vorwissen.",
          },
          {
            question: "Wie sicher sind KI-Lösungen?",
            answer:
              "Sicherheit hat bei uns höchste Priorität. Alle unsere Lösungen sind DSGVO-konform und werden in Deutschland gehostet. Du behältst immer die volle Kontrolle über deine Daten.",
          },
          {
            question: "Welche Branchen beratet ihr?",
            answer:
              "Wir haben Erfahrung in verschiedensten Branchen - vom Mittelstand bis zu Bildungseinrichtungen. KI-Potenziale gibt es überall, wir finden die passende Lösung für deine spezifischen Anforderungen.",
          },
          {
            question: "Wie schnell sehen wir erste Ergebnisse?",
            answer:
              "Bereits nach der Potenzialanalyse hast du einen klaren Überblick über mögliche Optimierungen. Die ersten KI-Lösungen können oft schon nach 4-6 Wochen produktiv eingesetzt werden.",
          },
        ],
      },
    },

    // Implementierung
    implementierung: {
      metadata: {
        title: "Maßgeschneiderte KI-Implementierung",
        description:
          "Von der Planung bis zur Integration: Wir implementieren KI nahtlos in deine bestehenden Systeme. DSGVO-konform, sicher und effizient.",
      },

      problems: {
        heading: "KI-Integration, die wirklich funktioniert",
        subheading:
          "Wir machen KI-Systeme zu einem nahtlosen Teil deiner Geschäftsprozesse",
        badge: "Technische Herausforderungen",
        title: "Diese technischen Hürden meistern wir",
        description:
          "Die Integration von KI in bestehende Systeme bringt spezifische Herausforderungen mit sich. Wir haben die Expertise, diese zu lösen",
        items: [
          {
            title: "Komplexe Integration",
            description:
              "Bestehende Systeme sind über Jahre gewachsen. Schnittstellen sind oft unübersichtlich und schwer zu verbinden.",
          },
          {
            title: "Datensicherheit",
            description:
              "Sensible Unternehmensdaten müssen geschützt werden. DSGVO-Konformität ist dabei ein absolutes Muss.",
          },
          {
            title: "Mitarbeiter-Akzeptanz",
            description:
              "Skepsis gegenüber KI-Systemen und Angst vor Jobverlust müssen sensibel adressiert werden.",
          },
          {
            title: "Implementierungsaufwand",
            description:
              "Begrenzte IT-Ressourcen und laufender Betrieb dürfen nicht beeinträchtigt werden.",
          },
        ],
      },
      services: {
        badge: "Lösungen",
        title: "Unsere Implementierungslösungen",
        description:
          "Mit unseren vier Kernkompetenzen stellen wir sicher, dass deine KI-Integration reibungslos und erfolgreich verläuft",
        items: [
          {
            title: "Nahtlose Integration",
            description:
              "Wir integrieren KI direkt in deine bestehenden Systeme - ohne komplizierte Umstellungen. Unsere Lösungen passen sich an deine IT-Landschaft an, nicht umgekehrt.",
            image: "/implementation/implementation_1.png",
          },
          {
            title: "Sichere Implementierung",
            description:
              "DSGVO-konforme Architektur von Anfang an. Alle Daten bleiben in deiner Kontrolle, gehostet in Deutschland. Maximale Sicherheit für deine Unternehmensdaten.",
            image: "/implementation/implementation_2.png",
          },
          {
            title: "Mitarbeiter-Enablement",
            description:
              "Umfassende Schulungen und schrittweise Einführung. Wir nehmen deine Mitarbeiter von Anfang an mit und zeigen ihnen, wie KI ihre Arbeit erleichtert.",
            image: "/implementation/implementation_3.png",
          },
          {
            title: "Schnelle Resultate",
            description:
              "Erste Erfolge in 4-6 Wochen. Durch unseren agilen Ansatz siehst du schnell messbare Verbesserungen, während wir die Lösung kontinuierlich optimieren.",
            image: "/implementation/implementation_4.png",
          },
        ],
      },
      integrations: {
        badge: "Erfolgreiche Integrationsbeispiele",
        title: "Bewährte KI-Automatisierungen für deine Prozesse",
        description:
          "Entdecke, wie unsere Kunden bereits von maßgeschneiderten KI-Lösungen profitieren - von der Buchhaltung bis zum Marketing, diese Beispiele zeigen reale Erfolge",
        labels: {
          challenge: "Deine Herausforderung",
          solution: "Unsere Lösung",
          benefit: "Dein Vorteil",
        },
        items: [
          {
            title: "Automatisierte Buchhaltung",
            challenge:
              "Monatlich stapeln sich Belege, während dein Team wertvolle Zeit mit manueller Eingabe und Kontrolle verbringt.",
            solution:
              "Direkte API-Anbindung an dein Buchhaltungssystem – unsere KI erfasst, kategorisiert und verbucht alle Belege automatisch.",
            benefit:
              "85% weniger Zeit für Buchhaltungsprozesse, präzise Zuordnung und ein jederzeit aktueller Finanzüberblick.",
          },
          {
            title: "Research & Marktanalyse",
            challenge:
              "Entscheidungen basieren oft auf unvollständigen Informationen – während umfassende Marktanalysen zu zeit- und ressourcenintensiv sind.",
            solution:
              "KI-gestützte Informationsaggregation aus Tausenden von Quellen, maßgeschneidert auf deine Branche und Fragestellungen.",
            benefit:
              "Fundierte Entscheidungen durch automatisch generierte Reports mit 92% mehr Datenabdeckung in nur einem Zehntel der Zeit.",
          },
          {
            title: "Content & Marketing",
            challenge:
              "Konsistenter, qualitativ hochwertiger Content auf allen Kanälen ist personalintensiv und schwer zu skalieren.",
            solution:
              "Integration in deine Marketing-Plattformen für KI-generierte Inhalte, automatisierte Kampagnen und datengestützte Optimierung.",
            benefit:
              "3x mehr Content-Output bei gleichbleibender Teamgröße und 28% höhere Engagement-Raten durch personalisierte Kommunikation.",
          },
          {
            title: "Intelligente Dokumentenverarbeitung",
            challenge:
              "Wichtige Informationen stecken in unterschiedlichen Dokumentformaten und müssen mühsam manuell extrahiert werden.",
            solution:
              "Nahtlose Integration in deine Dokumentenmanagementsysteme mit automatischer Extraktion, Klassifizierung und Weiterverarbeitung.",
            benefit:
              "94% Genauigkeit bei der Datenextraktion und 76% schnellere Verarbeitung von Verträgen, Angeboten und Formularen.",
          },
          {
            title: "E-Mail Automation",
            challenge:
              "Der tägliche E-Mail-Tsunami frisst wertvolle Arbeitszeit und wichtige Nachrichten gehen zwischen unwichtigen unter.",
            solution:
              "Direktes Andocken an deinen E-Mail-Client für intelligente Kategorisierung, automatische Antwortvorschläge und Priorisierung.",
            benefit:
              "Durchschnittlich 72 Minuten gesparte Zeit pro Mitarbeiter und Tag durch 60% weniger E-Mail-Unterbrechungen.",
          },
          {
            title: "Terminplanung & Ressourcenmanagement",
            challenge:
              "Terminkoordination ist zeitraubend, fehleranfällig und oft mit zahlreichen Rückfragen verbunden.",
            solution:
              "KI-gesteuerte Integration in deine Kalendersysteme für automatische Terminvorschläge, Raumplanung und Ressourcenzuweisung.",
            benefit:
              "Eliminierung von Terminüberschneidungen und 64% weniger Aufwand für Meeting-Organisation.",
          },
        ],
      },
      process: {
        badge: "Prozess",
        title: "Dein Weg zur erfolgreichen KI-Integration",
        description:
          "In vier klar definierten Phasen begleiten wir dich von der ersten Analyse bis zum erfolgreichen Produktivbetrieb",
        phases: [
          {
            subTitle: "Phase 1",
            title: "System-Analyse",
            description:
              "Wir analysieren deine bestehenden Systeme und Prozesse im Detail. Eine gründliche Bestandsaufnahme und Schnittstellenanalyse bildet die Basis für eine erfolgreiche Integration.",
            image: "/implementation/implementation_5.png",
          },
          {
            subTitle: "Phase 2",
            title: "Proof of Concept",
            description:
              "Mit einer Testintegration zeigen wir dir schnell erste Erfolge. In einem abgegrenzten Bereich demonstrieren wir die Wirksamkeit der KI-Lösung unter realen Bedingungen.",
            image: "/implementation/implementation_6.png",
          },
          {
            subTitle: "Phase 3",
            title: "Rollout-Plan",
            description:
              "Die schrittweise Einführung wird sorgfältig geplant. Parallel starten wir mit den Mitarbeiterschulungen, damit dein Team von Anfang an sicher mit der neuen Lösung arbeiten kann.",
            image: "/implementation/implementation_7.png",
          },
          {
            subTitle: "Phase 4",
            title: "Go-Live & Optimierung",
            description:
              "Nach erfolgreichem Produktivstart optimieren wir die Lösung kontinuierlich. Durch regelmäßiges Monitoring und Anpassungen stellen wir sicher, dass du maximalen Nutzen aus der KI-Integration ziehst.",
            image: "/implementation/implementation_8.png",
          },
        ],
      },
      faq: {
        badge: "FAQ",
        title: "Häufig gestellte Fragen zur KI-Implementation",
        description:
          "Hier findest du Antworten auf die wichtigsten Fragen rund um unsere KI-Implementierung",
        items: [
          {
            question: "Welche Systeme können angebunden werden?",
            answer:
              "Wir integrieren KI in nahezu alle gängigen Systeme - von DATEV über CRM-Systeme bis zu individuellen Unternehmenslösungen. Unsere Schnittstellen sind flexibel und anpassungsfähig.",
          },
          {
            question: "Wie lange dauert die Implementation?",
            answer:
              "Nach der initialen Analyse von 1-2 Wochen sehen wir bereits nach 4-6 Wochen erste produktive Ergebnisse. Die vollständige Integration erfolgt schrittweise und orientiert sich an deinen Prioritäten.",
          },
          {
            question: "Was kostet die Implementation?",
            answer:
              "Die Kosten richten sich nach Umfang und Komplexität der Integration. Nach der kostenlosen Erstanalyse erstellen wir dir ein maßgeschneidertes Angebot mit klarer ROI-Berechnung.",
          },
          {
            question: "Wie werden die Mitarbeiter eingebunden?",
            answer:
              "Wir bieten umfassende Schulungen und eine schrittweise Einführung. Dein Team wird von Anfang an mitgenommen und lernt, die KI-Systeme optimal für ihre Arbeit zu nutzen.",
          },
          {
            question: "Wie sicher sind die implementierten Systeme?",
            answer:
              "Sicherheit hat höchste Priorität. Alle Systeme werden DSGVO-konform in Deutschland gehostet. Du behältst die volle Kontrolle über deine Daten und Prozesse.",
          },
          {
            question: "Welche Wartung ist nach der Implementation nötig?",
            answer:
              "Wir bieten kontinuierliches Monitoring und regelmäßige Updates. Durch proaktive Wartung und Optimierung stellen wir sicher, dass deine KI-Systeme immer optimal laufen.",
          },
        ],
      },
    },

    // Karriere
    karriere: {
      metadata: {
        title: "Gestalte die Zukunft der KI mit",
        description:
          "Werde Teil unseres innovativen Teams und hilf Unternehmen bei ihrer KI-Transformation. Offene Positionen für Visionäre und Macher.",
      },

      careers: {
        heading: "Gestalte mit uns die KI-Zukunft",
        description:
          "Wir suchen Menschen, die mit uns die Zukunft der KI in Deutschland gestalten wollen. Aktuell sind keine Positionen ausgeschrieben, aber wir freuen uns über deine Initiativbewerbung.",
        noPositions: {
          title: "Keine offenen Stellen verfügbar",
          description: "Bald werden hier neue Stellenangebote veröffentlicht.",
        },
      },
    },

    // Kontakt
    kontakt: {
      metadata: {
        title: "Starte deine KI-Transformation",
        description:
          "Bereit für den nächsten Schritt? Lass uns gemeinsam dein KI-Potenzial entdecken. Vereinbare jetzt deine kostenlose ZAVI Potenzialanalyse.",
      },
      contact: {
        heading: "Lass uns über dein KI-Potenzial sprechen",
        description:
          "Wir sind hier, um dir zu helfen – kontaktiere uns für eine kostenlose Ersteinschätzung oder mit deinen Fragen",
        contactMethods: [
          {
            title: "E-Mail",
            description:
              "Du hast Fragen zu unseren KI-Lösungen? Schreib uns eine E-Mail, wir antworten innerhalb von 24 Stunden.",
            contact: "kontakt@zavi-ai.de",
          },
          {
            title: "Standort",
            description:
              "Made in Germany: Unsere KI-Lösungen entwickeln wir in Köln.",
            contact: "",
          },
        ],
        form: {
          fields: [
            {
              id: "name",
              label: "Dein Name",
            },
            {
              id: "email",
              label: "Deine E-Mail",
            },
            {
              id: "company",
              label: "Unternehmen",
            },
            {
              id: "message",
              label: "Deine Nachricht",
              placeholder:
                "Wie können wir dir helfen? Erzähl uns von deinem Projekt...",
            },
          ],
          datenschutz: {
            label: "Ich akzeptiere die Datenschutzerklärung",
            link: "/datenschutz",
          },
          turnstile: {
            consentText:
              "Um unser Kontaktformular vor Spam zu schützen, verwenden wir Cloudflare Turnstile. Bitte aktiviere die Sicherheits-Cookies in deinen Cookie-Einstellungen.",
            buttonText: "Cookie-Einstellungen öffnen",
          },
          submitButton: {
            submitting: "Wird gesendet...",
            default: "Nachricht senden",
          },
          messages: {
            success:
              "Deine Nachricht wurde erfolgreich gesendet. Wir melden uns in Kürze bei dir.",
            turnstileError: "Bitte bestätige, dass du kein Roboter bist.",
            datenschutzError: "Bitte akzeptiere die Datenschutzerklärung.",
            turnstileConsentError:
              "Bitte akzeptiere die Sicherheits-Cookies in den Cookie-Einstellungen, um das Formular absenden zu können.",
            generalError:
              "Beim Senden deiner Nachricht ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
          },
        },
      },
    },

    // Projekte
    projekte: {
      metadata: {
        title: "KI-Erfolgsgeschichten und Referenzprojekte",
        description:
          "Von StudyLabs bis Mittelstand: Erfahre, wie unsere KI-Lösungen Unternehmen effizienter machen. Praxisnahe Beispiele für deine eigene KI-Transformation.",
      },
      overview: {
        heading: "Innovative KI-Projekte von ZAVI",
        description:
          "Entdecke unsere wegweisenden KI-Lösungen, die neue Standards in verschiedenen Branchen setzen",
        projects: [
          {
            image: "/StudyLabs_Screen_OpenGraph.png",
            link: "/studylabs",
            title: "StudyLabs",
            description:
              "Der erste KI-Agent für die Hochschullehre. Wissenschaftlich validierte Technologie für automatisierte Bewertung und personalisiertes Feedback, entwickelt in Zusammenarbeit mit führenden deutschen Universitäten.",
          },
        ],
      },
    },

    // StudyLabs
    studylabs: {
      metadata: {
        title: "Studylabs",
        description: "Studylabs: Der erste KI Agent in der Lehre",
      },
      hero: {
        heading: "Der erste KI-Agent, der Lehre revolutioniert",
        badge: {
          text: "Hier geht's zu StudyLabs!",
          url: "https://studylabs.app",
        },
        description:
          "Wissenschaftlich validierte KI-Technologie für automatisierte Bewertung und personalisiertes Feedback",
        video: {
          src: "https://www.studylabs.app/StudyLabs_Demo_Video.mp4",
          fallbackText: "Your browser does not support the video tag.",
        },
        integrations: [
          {
            title: "Intelligente Bewertung",
            description:
              "AR-LLMs für präzise Korrektur von Freitextaufgaben. 94% Übereinstimmung mit manueller Bewertung durch Experten.",
          },
          {
            title: "Personalisiertes Feedback",
            description:
              "Sofortiges, detailliertes Feedback für jeden Studierenden. Individuelle Lernempfehlungen für optimalen Lernerfolg.",
          },
          {
            title: "Teaching Companion",
            description:
              "KI-Chatbot mit Zugriff auf alle Kursmaterialien. 24/7 intelligente Unterstützung und kontextbezogene Antworten für Studierende.",
          },
          {
            title: "Wissenschaftlich validiert",
            description:
              "Im IJRM publiziert und in Zusammenarbeit mit führenden Universitäten entwickelt. Höchste akademische Standards.",
            link: {
              url: "https://www.sciencedirect.com/science/article/pii/S0167811624000430",
              text: "Im IJRM publiziert",
            },
          },
        ],
      },
      problems: {
        badge: "Herausforderungen",
        heading: "Aktuelle Herausforderungen in der Lehre",
        description:
          "Diese Probleme erschweren heute effektives Lehren und Lernen an Hochschulen",
        items: [
          {
            title: "Korrekturaufwand",
            description:
              "Stundenlange manuelle Korrekturen verzögern wichtiges Feedback und binden wertvolle Ressourcen der Lehrenden.",
          },
          {
            title: "Betreuungsqualität",
            description:
              "Steigende Studierendenzahlen erschweren individuelle Betreuung und persönliches Feedback.",
          },
          {
            title: "Standardisierung",
            description:
              "Schwankende Bewertungsqualität und mangelnde Vergleichbarkeit zwischen verschiedenen Korrektoren.",
          },
          {
            title: "Skalierbarkeit",
            description:
              "Begrenzte Kapazitäten für innovative Lehrformate und interaktive Übungsaufgaben.",
          },
        ],
      },
      vision: {
        badge: "Vision",
        heading: "Die Zukunft der Hochschullehre",
        description:
          "Wir gestalten eine Zukunft, in der KI Lehrende unterstützt und Studierende optimal fördert",
        items: [
          {
            title: "Entlastung der Lehrenden",
            description: [
              "Automatisierte Bewertung von Freitextaufgaben reduziert den Korrekturaufwand drastisch.",
              "Lehrende können sich auf die persönliche Betreuung und Wissensvermittlung konzentrieren.",
            ],
            image: {
              src: "/studylabs/studylabs_1.png",
              alt: "Entlastung der Lehrenden",
            },
          },
          {
            title: "Sofortiges Feedback",
            description: [
              "Studierende erhalten unmittelbar detaillierte Rückmeldungen zu ihren Aufgaben.",
              "Personalisierte Lernempfehlungen unterstützen den individuellen Lernfortschritt.",
            ],
            image: {
              src: "/studylabs/studylabs_2.png",
              alt: "Sofortiges Feedback",
            },
          },
          {
            title: "Skalierbare Qualität",
            description: [
              "Wissenschaftlich validierte Bewertungen garantieren faire und einheitliche Standards für alle Studierenden.",
            ],
            image: {
              src: "/studylabs/studylabs_3.png",
              alt: "Skalierbare Qualität",
            },
          },
          {
            title: "Teaching Companion",
            description: [
              "KI-Chatbot als persönlicher Lernbegleiter. Rund um die Uhr Zugriff auf Kursmaterialien und intelligente Unterstützung.",
            ],
            image: {
              src: "/studylabs/studylabs_4.png",
              alt: "KI als Unterstützung",
            },
          },
        ],
      },
      timeline: {
        badge: "Entwicklung",
        heading: "Die Entwicklung von StudyLabs",
        description:
          "Von der ersten Vision bis zur wissenschaftlich validierten KI-Lösung für die Hochschullehre",
        sections: [
          {
            subTitle: "2023",
            title: "Konzeption & Erste Partnerschaft",
            description:
              "Entwicklung des ersten Konzepts für automatisierte Bewertungen. Partnerschaft mit der Goethe-Universität Frankfurt und Prof. Dr. Bernd Skiera für die erste Testphase.",
          },
          {
            subTitle: "Frühjahr 2024",
            title: "Erfolgreicher Pilottest",
            description:
              "Erste Implementation in der Veranstaltung BEOM Marketing Analytics. Entwicklung der AR-LLMs für präzise Korrektur von Freitextaufgaben.",
          },
          {
            subTitle: "Mitte 2024",
            title: "Expansion & Validierung",
            description:
              "Ausweitung auf die LMU München. Zusammenarbeit mit Dr. Anna-Carolina Haensch. Praxistest mit über 300 Studierenden und kontinuierliche Optimierung.",
          },
          {
            subTitle: "September 2024",
            title: "Teaching Companion Launch",
            description:
              "Einführung des KI-Chatbots mit Zugriff auf alle Kursmaterialien. Studierende erhalten rund um die Uhr intelligente Unterstützung und kontextbezogene Antworten.",
          },
          {
            subTitle: "Anfang 2025",
            title: "Deutschlandweite Expansion",
            description:
              "Integration an führenden Hochschulen: DHGS, HAM, Uni Seeburg und H2. StudyLabs etabliert sich als Standard für KI-gestützte Hochschullehre.",
          },
        ],
      },
      stats: {
        badge: "Das Ergebnis",
        heading: "Wissenschaftlich validierte Ergebnisse",
        description:
          "Unsere Erfolge basieren auf messbaren Daten und wissenschaftlicher Validierung",
        items: [
          {
            value: "94%",
            description: "Übereinstimmung mit manuellen Korrekturen",
          },
          {
            value: "300+",
            description: "Studierende im Pilotprojekt",
          },
          {
            value: "6",
            description: "Universitäten & Hochschulen",
          },
          {
            value: "24/7",
            description: "KI-Unterstützung durch Teaching Companion",
          },
        ],
      },
    },

    // Über Uns
    über_uns: {
      metadata: {
        title: "Dein Partner für KI-Innovation Made in Germany",
        description:
          "Entdecke die Geschichte und Vision von ZAVI. Ein junges, dynamisches Team macht KI greifbar für den deutschen Mittelstand – transparent, sicher und zukunftsorientiert.",
      },
      hero: {
        heading: "KI verändert die Welt – wir machen sie greifbar",
        description1:
          "Wir haben es uns zur Aufgabe gemacht, Unternehmen nicht nur in die KI-Ära zu bringen, sondern sie darin führend zu machen.",
        description2:
          "Wir sind kein weiteres KI-Startup mit leeren Versprechen – wir liefern echte, greifbare Lösungen, die sofort wirken. Mit DSGVO-konformen Systemen, direkter Integration und langfristiger Partnerschaft machen wir Unternehmen fit für die KI-Zukunft.",
        images: [
          {
            src: "/about/about_1.png",
            alt: "KI Innovation",
          },
          {
            src: "/about/about_2.png",
            alt: "Technologie Expertise",
          },
        ],
      },
      story: {
        badge: {
          text: "Unsere Geschichte",
        },
        heading: "Von der Idee zur Innovation",
        description:
          "Unsere Reise begann 2023 mit dem Ziel, KI wirklich nutzbar zu machen – heute sind wir Partner für die digitale Transformation",
        paragraph1:
          "2023: Wir starteten mit ersten KI-Anwendungen und SaaS-Produkten. Schnell wurde uns klar: KI ist mächtig, aber schwer greifbar. Viele Unternehmen sehen das Potenzial, wissen aber nicht, wie sie es nutzen können.",
        paragraph2:
          "2023–2025: Mit StudyLabs entwickelten wir eine der ersten erfolgreichen KI-gestützten Lernplattformen. Gleichzeitig forschten wir an neuen Automatisierungslösungen, die über einfache Chatbots hinausgehen. Heute arbeiten wir mit dem Mittelstand und hochkomplexen Unternehmensprozessen.",
        paragraph3:
          "Unser Ziel ist es, Unternehmen effizienter, profitabler und wettbewerbsfähiger zu machen – mit greifbarer, maßgeschneiderter KI.",
        paragraph4:
          "Wir glauben an eine Zukunft, in der Unternehmen durch KI gestärkt werden. Automatisierung bedeutet nicht, Menschen zu ersetzen – sondern sie von ineffizienten Prozessen zu befreien.",
        images: [
          {
            src: "/about/about_3.png",
            alt: "about 1",
          },
          {
            src: "/about/about_4.png",
            alt: "about 2",
          },
          {
            src: "/about/about_5.png",
            alt: "about 3",
          },
          {
            src: "/about/about_6.png",
            alt: "about 4",
          },
          {
            src: "/about/about_7.png",
            alt: "about 5",
          },
          {
            src: "/about/about_8.png",
            alt: "about 6",
          },
        ],
      },
      team: {
        badge: {
          text: "Unser Team",
        },
        heading: "Das Team hinter ZAVI",
        description:
          "Ein Team, das sich leidenschaftlich für KI, Automatisierung und Unternehmenswachstum einsetzt",
        members: [
          {
            name: "Karim Zibo",
            role: "CEO & KI-Architekt",
            image: "/about/about_karim.png",
            bio: "Experte für KI-Architektur und Unternehmensprozesse. Treibt die Vision von ZAVI voran, KI greifbar und nutzbar zu machen.",
            social: {
              linkedin: "https://www.linkedin.com/in/karim-zibo/",
            },
          },
          {
            name: "Matti Vennen",
            role: "CTO & Tech-Strategie",
            image: "/about/about_matti.jpeg",
            bio: "Verantwortlich für die technische Umsetzung und Skalierung unserer KI-Lösungen. Fokus auf DSGVO-konforme Implementierung.",
            social: {
              linkedin: "https://www.linkedin.com/in/matti-vennen-98676a274/",
            },
          },
          {
            name: "Timo Jenne",
            role: "CMO & Marketing",
            image: "/about/about_timo.jpg",
            bio: "Leitet die Marketingstrategie und Kommunikation von ZAVI. Fokus auf Markenaufbau und die Vermittlung komplexer KI-Themen in verständlicher Form.",
            social: {
              linkedin: "https://www.linkedin.com/in/timo-jenne-1254a61aa/",
            },
          },
        ],
      },
      values: {
        badge: {
          text: "Unsere Werte",
        },
        heading: "Was uns auszeichnet",
        description:
          "Wir setzen auf langfristige Partnerschaften, höchste Sicherheitsstandards und einen menschenzentrierten Ansatz bei der KI-Integration",
        items: [
          {
            title: "Direkte Integration",
            description:
              "Wir arbeiten mit deinen bestehenden Systemen. Keine komplizierte Drittsoftware, sondern nahtlose Integration in deine Prozesse.",
          },
          {
            title: "Made in Germany",
            description:
              "Datenschutz & DSGVO-konform. Entwickelt und gehostet in Deutschland für maximale Sicherheit deiner Daten.",
          },
          {
            title: "Langfristige Partnerschaft",
            description:
              "Wir begleiten dich kontinuierlich auf deinem Weg zur KI-Integration. Mit regelmäßigen Updates und strategischer Beratung.",
          },
          {
            title: "Mensch im Mittelpunkt",
            description:
              "KI soll Menschen unterstützen, nicht ersetzen. Wir setzen KI dort ein, wo sie Mitarbeiter von ineffizienten Prozessen befreit.",
          },
        ],
      },
    },

    // Analysen
    analysen: {
      metadata: {
        title: "KI-Analysen und Experten-Insights",
        description:
          "Tiefgehende Einblicke in KI-Trends und -Technologien. Praxisnahe Analysen für Entscheider im deutschen Mittelstand.",
      },
      analysen: {
        heading: "Unsere Branchenanalysen",
        description:
          "Entdecke unsere branchenspezifischen Analysen, die dir helfen, branchenspezifische KI-Potenziale und konkrete Anwendungsfälle für dein Unternehmen zu finden.",
        noAnalysen: {
          title: "Keine Branchenanalysen gefunden",
          description: "Bald werden hier neue Branchenanalysen veröffentlicht.",
        },
      },
      layout: {
        backLink: "Zurück",
        featuringInsights: "Featuring insights from:",
        overview: "Überblick",
        category: "Kategorie",
        share: "Teilen:",
        socialLinks: {
          linkedin: "https://www.linkedin.com/company/zavi-ai/",
          twitter: "https://x.com/zavi_ai",
          facebook: "https://www.facebook.com/zavi.ai",
        },
        scrollToTop: "Nach oben",
        onThisPage: "Auf dieser Seite",
        defaultTitle: "How this tool helps teams achieve efficient workflows",
      },
    },

    // News
    news: {
      metadata: {
        title: "KI-News und Updates",
        description:
          "Bleib auf dem Laufenden über die neuesten Entwicklungen in der KI-Welt. Praxisnahe Insights und Updates von ZAVI.",
      },
      news: {
        heading: "Die neusten KI-News",
        description:
          "Bleibe auf dem Laufenden mit den neuesten Entwicklungen und Trends in der KI-Welt – fundiert recherchiert und verständlich für dich erklärt.",
        badge: {
          text: "Unser Neuster Beitrag",
        },
        tabs: {
          all: "Alle",
          design: "Design",
          product: "Produkt",
          softwareEngineering: "Software Engineering",
          customerSuccess: "Customer Success",
        },
        sort: {
          placeholder: "Sortieren",
          recent: "Neueste",
          oldest: "Älteste",
        },
        authorInfo: {
          writtenBy: "Geschrieben von",
          publishedOn: "Veröffentlicht am",
          fileUnder: "Kategorie",
        },
        pagination: {
          previous: "Zurück",
          next: "Weiter",
        },
        noNews: {
          title: "Keine KI-News gefunden",
          description: "Bald werden hier neue KI-News veröffentlicht.",
        },
      },
      layout: {
        backLink: "Zurück",
        inThisArticle: "In diesem Artikel",
        shareArticle: "Artikel teilen",
        scrollToTop: "Nach oben",
        socialLinks: {
          facebook: "#",
          twitter: "#",
          linkedin: "#",
        },
      },
    },

    // Datenschutz
    datenschutz: {
      metadata: {
        title: "Datenschutzerklärung",
        description:
          "Erfahre mehr über unseren Umgang mit deinen Daten. DSGVO-konforme Datenschutzrichtlinien von ZAVI.",
      },
      content: {
        title: "Datenschutzerklärung",
        intro:
          "Bei ZAVI nehmen wir den Schutz Ihrer Privatsphäre sehr ernst. Diese Datenschutzrichtlinie erläutert, wie wir Ihre persönlichen Daten erheben, verwenden und schützen, wenn Sie unsere Website besuchen oder unsere Dienstleistungen nutzen.",
        sections: [
          {
            title: "1. Welche Informationen wir erfassen",
            text: "Wir können die folgenden Arten von Informationen sammeln:",
            items: [
              "Persönliche Identifikationsdaten: Name, E-Mail-Adresse, Telefonnummer und andere Kontaktdaten, die Sie freiwillig bereitstellen.",
              "Nutzungsdaten: Informationen darüber, wie Sie mit unserer Website interagieren, einschließlich Ihrer IP-Adresse, des verwendeten Browsertyps, der besuchten Seiten und der Verweildauer auf unserer Website.",
              "Cookies und Tracking-Technologien: Wir verwenden Cookies, um Ihr Nutzererlebnis zu verbessern. Sie können Ihre Cookie-Präferenzen über die Einstellungen Ihres Browsers verwalten.",
            ],
          },
          {
            title: "2. Wie wir Ihre Daten nutzen",
            text: "Wir verwenden die gesammelten Informationen für folgende Zwecke:",
            items: [
              "Bereitstellung und Verbesserung unserer Dienste: Personalisierung Ihres Nutzererlebnisses und Optimierung unserer Angebote.",
              "Kommunikation: Versand von Updates, Newslettern und Beantwortung von Anfragen.",
              "Analyse: Analyse der Website-Nutzung zur Verbesserung der Benutzerfreundlichkeit.",
            ],
          },
          {
            title: "3. Datenweitergabe und Offenlegung",
            text: "Wir verkaufen oder vermieten Ihre persönlichen Daten nicht. Eine Weitergabe an Dritte erfolgt nur in folgenden Fällen:",
            items: [
              "Dienstleister: Vertrauenswürdige Partner, die uns bei der Bereitstellung und dem Betrieb unserer Website und Dienste unterstützen. Diese sind durch Vertraulichkeitsvereinbarungen gebunden.",
              "Rechtliche Anforderungen: Wenn dies gesetzlich vorgeschrieben ist oder um unsere Rechte zu schützen.",
            ],
          },
          {
            title: "4. Datensicherheit",
            text: "Wir setzen geeignete Sicherheitsmaßnahmen ein, um Ihre Daten zu schützen. Bitte beachten Sie jedoch, dass keine Methode der Datenübertragung über das Internet vollständig sicher ist.",
          },
          {
            title: "5. Ihre Rechte",
            text: "Sie haben das Recht, auf Ihre persönlichen Daten zuzugreifen, diese zu korrigieren oder zu löschen. Wenden Sie sich hierzu bitte an uns unter:",
          },
          {
            title: "6. Änderungen dieser Datenschutzrichtlinie",
            text: "Wir können diese Datenschutzrichtlinie von Zeit zu Zeit aktualisieren. Änderungen werden auf dieser Seite veröffentlicht und mit einem aktualisierten Revisionsdatum versehen.",
          },
          {
            title: "7. Kontakt",
            text: "Wenn Sie Fragen zu dieser Datenschutzrichtlinie haben, können Sie uns unter den folgenden Kontaktmöglichkeiten erreichen:",
          },
          {
            title: "8. Cloudflare Turnstile",
            text: "Unsere Website nutzt Cloudflare Turnstile, einen Dienst zur Erkennung von Bots und zum Schutz unserer Formulare.",
            paragraph:
              "Beim Ausfüllen von Formularen auf unserer Website kann Turnstile aktiviert werden, um zu überprüfen, dass ein Mensch und nicht ein automatisiertes System das Formular ausfüllt. Dabei werden Informationen über Ihr Gerät und Ihr Verhalten auf der Website an Cloudflare übermittelt, einschließlich:",
            items: [
              "IP-Adresse",
              "Informationen zum Browser und Betriebssystem",
              "Browser-Fingerprint",
              "Cookies, die von Cloudflare gesetzt werden",
            ],
            footer:
              "Die Datenverarbeitung erfolgt auf Grundlage unserer berechtigten Interessen (Art. 6 Abs. 1 lit. f DSGVO), um unsere Website vor missbräuchlicher automatisierter Nutzung und Spam zu schützen. Weitere Informationen zu Cloudflare Turnstile und deren Datenschutzpraktiken finden Sie unter:",
          },
          {
            title: "9. Google Analytics",
            text: 'Wir nutzen Google Analytics, einen Webanalysedienst der Google LLC ("Google"), um unsere Website zu optimieren. Google Analytics verwendet Cookies und ähnliche Technologien, um Informationen über die Nutzung unserer Website zu sammeln und zu analysieren.',
            paragraph1:
              "Die durch Google Analytics erfassten Informationen umfassen:",
            items: [
              "Ihre anonymisierte IP-Adresse (wir haben die IP-Anonymisierung aktiviert)",
              "Informationen über Ihren Browser, Betriebssystem und Gerät",
              "Zeitpunkt und Dauer Ihres Besuchs",
              "Besuchte Seiten und Interaktionen auf unserer Website",
              "Verweisende Websites oder Suchbegriffe",
            ],
            paragraph2:
              "Diese Daten werden auf Google-Servern gespeichert und zur Erstellung von Berichten über Website-Aktivitäten und zur Erbringung weiterer mit der Website- und Internetnutzung verbundenen Dienstleistungen verwendet.",
            paragraph3:
              "Sie können die Erfassung Ihrer Daten durch Google Analytics verhindern, indem Sie das Browser-Add-on zur Deaktivierung von Google Analytics herunterladen und installieren:",
            paragraph4:
              "Weitere Informationen zum Umgang mit Nutzerdaten bei Google Analytics finden Sie in der Datenschutzerklärung von Google:",
          },
        ],
      },
      links: {
        email: "mailto:kontakt@zavi-ai.com",
        cloudflare: "https://www.cloudflare.com/de-de/privacypolicy/",
        googleAnalyticsOptOut: "https://tools.google.com/dlpage/gaoptout",
        googlePrivacy: "https://policies.google.com/privacy",
      },
    },

    // AGB
    agb: {
      metadata: {
        title: "Allgemeine Geschäftsbedingungen",
        description:
          "Unsere AGBs im Überblick. Transparent und verständlich formulierte Geschäftsbedingungen für ZAVI Dienstleistungen.",
      },
      agb: {
        title: "Allgemeine Geschäftsbedingungen",
        intro:
          "Willkommen bei ZAVI! Diese Allgemeinen Geschäftsbedingungen (&quot;AGB&quot;) regeln die Nutzung der Website und Dienstleistungen von ZAVI. Durch den Zugriff auf oder die Nutzung unserer Website und Dienstleistungen erklären Sie sich mit diesen AGB einverstanden.",
        sections: [
          {
            title: "1. Definitionen",
            content: [
              "&quot;Wir,&quot; &quot;Uns,&quot; &quot;Unser&quot;: Bezieht sich auf ZAVI.",
              "&quot;Kunde&quot; oder &quot;Sie&quot;: Bezieht sich auf die Person oder Organisation, die unsere Dienstleistungen nutzt.",
              "&quot;Dienstleistungen&quot;: Bezieht sich auf die von ZAVI angebotenen KI- und Automatisierungslösungen, einschließlich Beratung, Entwicklung, Integration und Wartung.",
            ],
          },
          {
            title: "2. Dienstleistungen",
            content: [
              "ZAVI bietet maßgeschneiderte KI-Lösungen, die speziell auf die Bedürfnisse Ihres Unternehmens zugeschnitten sind. Unsere Dienstleistungen umfassen, sind aber nicht beschränkt auf:",
              "Workflow-Analysen.",
              "Entwicklung und Integration von KI-Lösungen.",
              "Wartung und Optimierung nach der Bereitstellung.",
              "Spezifische Liefergegenstände und Zeitpläne werden in einer separaten Vereinbarung oder Projektbeschreibung festgelegt.",
            ],
            hasList: true,
          },
          {
            title: "3. Verantwortlichkeiten des Nutzers",
            content: [
              "Durch die Nutzung unserer Dienstleistungen verpflichten Sie sich:",
              "Genaue und vollständige Informationen bereitzustellen, die für die Projektdurchführung erforderlich sind.",
              "Unsere Lösungen im Einklang mit geltenden Gesetzen und Vorschriften zu verwenden.",
              "Missbrauch oder Reverse Engineering der bereitgestellten Lösungen zu unterlassen.",
            ],
            hasList: true,
          },
          {
            title: "4. Zahlungsbedingungen",
            content: [
              "Rechnungen sind innerhalb von 14 Tagen nach Erhalt fällig.",
              "Bei verspäteten Zahlungen kann ein Verzugszins in Höhe von 1% pro Monat berechnet werden.",
            ],
            hasList: true,
          },
          {
            title: "5. Geistiges Eigentum",
            content: [
              "Alle geistigen Eigentumsrechte an den von ZAVI erstellten Lösungen verbleiben bei ZAVI, bis die vollständige Zahlung erfolgt ist.",
              "Nach vollständiger Zahlung erhält der Kunde eine nicht-exklusive, nicht übertragbare Lizenz zur Nutzung der gelieferten Lösungen.",
            ],
            hasList: true,
          },
          {
            title: "6. Vertraulichkeit",
            content: [
              "Beide Parteien verpflichten sich, vertrauliche Informationen privat zu halten und ausschließlich für die Projektdurchführung zu verwenden. Dazu gehören Geschäftsdaten, proprietäre Prozesse und andere während des Projekts geteilte sensible Informationen.",
            ],
          },
          {
            title: "7. Haftungsbeschränkung",
            content: [
              "ZAVI haftet nicht für:",
              "Indirekte, zufällige oder Folgeschäden, die aus der Nutzung unserer Dienstleistungen entstehen.",
              "Verluste, die durch den Missbrauch der KI-Lösungen durch den Kunden oder Dritte entstehen.",
              "Verzögerungen oder Ausfälle, die durch Umstände außerhalb unserer Kontrolle verursacht werden, wie Naturkatastrophen oder technische Störungen.",
            ],
            hasList: true,
          },
          {
            title: "8. Kündigung",
            content: [
              "Beide Parteien können die Vereinbarung mit einer Frist von [Frist in Tagen] schriftlich kündigen. Im Falle einer Kündigung:",
              "Ist der Kunde verpflichtet, alle bis zum Kündigungsdatum erbrachten Leistungen zu bezahlen.",
              "Wird ZAVI alle bereits bezahlten und abgeschlossenen Arbeiten und Materialien übergeben.",
            ],
            hasList: true,
          },
          {
            title: "9. Änderungen",
            content: [
              "ZAVI behält sich das Recht vor, diese AGB jederzeit zu aktualisieren. Kunden werden über wesentliche Änderungen informiert. Die fortgesetzte Nutzung der Dienstleistungen nach solchen Änderungen gilt als Zustimmung zu den aktualisierten AGB.",
            ],
          },
          {
            title: "10. Anwendbares Recht",
            content: [
              "Diese AGB unterliegen dem Recht der Bundesrepublik Deutschland und sind nach diesem auszulegen.",
            ],
          },
          {
            title: "11. Kontakt",
            content: [
              "Bei Fragen zu diesen AGB können Sie uns wie folgt kontaktieren:",
              "E-Mail: kontakt@zavi-ai.com",
            ],
            contactEmail: "kontakt@zavi-ai.com",
          },
        ],
      },
    },

    // Impressum
    impressum: {
      metadata: {
        title: "Impressum",
        description: "Rechtliche Informationen und Kontaktdaten der ZAVI GmbH.",
      },
      content: {
        title: "Impressum",
        sections: [
          {
            title: "Verantwortlicher gemäß § 5 TMG",
            content: [
              "ZAVI GmbH",
              "Sömmeringstraße 21",
              "50823 Köln",
              "Deutschland",
              "E-Mail Adresse: kontakt@zavi-ai.com",
              "Geschäftsbereich: Sonstige Softwareentwicklung WZ2008 – 62.01.9",
            ],
          },
          {
            title: "Registergericht",
            content: ["Amtsgericht Köln", "HRB 122799"],
          },
          {
            title: "Geschäftsführer",
            content: ["Karim Zibo, Matti Vennen"],
          },
          {
            title: "Online-Streitbeilegung (OS)",
            content: [
              "Online-Streitbeilegung: Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit, die Sie unter https://ec.europa.eu/consumers/odr/ finden. Verbraucher haben die Möglichkeit, diese Plattform für die Beilegung ihrer Streitigkeiten zu nutzen.",
            ],
            links: {
              osPlattform: "https://ec.europa.eu/consumers/odr/",
            },
          },
          {
            title: "Social Media und andere Onlinepräsenzen",
            content: [
              "Dieses Impressum gilt auch für die folgenden Social-Media-Präsenzen und Onlineprofile:",
            ],
            links: {
              linkedin: "https://www.linkedin.com/company/zavi-ai/about/",
            },
          },
          {
            title: "Haftungs- und Schutzrechtshinweise",
            content: [
              "Haftungsausschluss: Die Inhalte dieses Onlineangebotes wurden sorgfältig und nach unserem aktuellen Kenntnisstand erstellt, dienen jedoch nur der Information und entfalten keine rechtlich bindende Wirkung, sofern es sich nicht um gesetzlich verpflichtende Informationen (z.B. das Impressum, die Datenschutzerklärung, AGB oder verpflichtende Belehrungen von Verbrauchern) handelt. Wir behalten uns vor, die Inhalte vollständig oder teilweise zu ändern oder zu löschen, soweit vertragliche Verpflichtungen unberührt bleiben. Alle Angebote sind freibleibend und unverbindlich.",
              "Link auf fremde Webseiten: Die Inhalte fremder Webseiten, auf die wir direkt oder indirekt verweisen, liegen außerhalb unseres Verantwortungsbereiches und wir machen sie uns nicht zu Eigen. Für alle Inhalte und Nachteile, die aus der Nutzung der in den verlinkten Webseiten aufrufbaren Informationen entstehen, übernehmen wir keine Verantwortung.",
              "Hinweise auf Rechtsverstöße: Sollten Sie innerhalb unseres Internetauftritts Rechtsverstöße bemerken, bitten wir Sie uns auf diese hinzuweisen. Wir werden rechtswidrige Inhalte und Links nach Kenntnisnahme unverzüglich entfernen.",
            ],
          },
        ],
      },
    },
  },
};
